import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AveragePlayerScore,
  AverageScoreTeamMode,
  AverageStat,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import type { AnyPlayerUpcomingGames_anyGameStat } from '#src/components/player/AnyPlayerUpcomingGames/__generated__/index.graphql.ts';
import { AnyPlayerUpcomingGames } from '#src/components/player/AnyPlayerUpcomingGames/index.tsx';
import { SearchPlayerCell } from '#src/components/searchPlayerTable/SearchPlayerCell/index.tsx';
import { AveragePlayerScoreCell } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/AveragePlayerScoreCell/index.tsx';
import { ProjectionGradeCell } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/ProjectionGradeCell/index.tsx';
import { SearchPlayerStatsCells } from '#src/components/searchPlayerTable/SearchPlayerStatsCells/index.tsx';
import {
  CellGrow,
  SearchTableRow,
} from '#src/components/searchPlayerTable/SearchTable/ui.tsx';
import { StartingPriceCell } from '#src/components/searchPlayerTable/StartingPriceCell/index.tsx';
import type { PlayerResultsProps } from '#src/searchCommonPlayer/AdvancedPlayerSearch/types.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';
import type { TeamMode } from '#src/searchCommonPlayer/teamMode.ts';

import type { SearchPlayerRow_anyPlayer } from './__generated__/index.graphql.ts';

type Props = Pick<PlayerResultsProps, 'watchlistId' | 'favoritesWatchlist'> & {
  anyPlayer: SearchPlayerRow_anyPlayer;
  onStatsClick: () => void;
  onListingsClick: () => void;
  rarity: Rarity;
  scopedScores?: Partial<Record<TotalAveragePlayerScore, number | null>>;
  scopedBreakdowns?: Partial<Record<AveragePlayerScore, number | null>>;
  scopedFootballAverageStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
  scopedFootballTotalStats?: readonly {
    stat: AverageStat;
    value: number | null;
  }[];
  scopedAnyGameStats?: readonly (AnyPlayerUpcomingGames_anyGameStat | null)[];
  recentGameTeamModes?: (TeamMode | null)[];
  teamMode?: AverageScoreTeamMode | null;
};

export const SearchPlayerRow = ({
  anyPlayer,
  onStatsClick,
  onListingsClick,
  rarity,
  watchlistId,
  favoritesWatchlist,
  scopedScores,
  scopedBreakdowns,
  scopedFootballAverageStats,
  scopedFootballTotalStats,
  scopedAnyGameStats,
  recentGameTeamModes,
  teamMode,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  return (
    <SearchTableRow gap={2}>
      <SearchPlayerCell
        anyPlayer={anyPlayer}
        onStatsClick={onStatsClick}
        watchlistId={watchlistId}
        favoritesWatchlist={favoritesWatchlist}
        recentGameTeamModes={recentGameTeamModes}
      />
      <AnyPlayerUpcomingGames
        anyPlayer={anyPlayer}
        scopedAnyGameStats={scopedAnyGameStats}
        teamMode={teamMode}
      />
      {statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].map(
        so5FixtureEvent => (
          <ProjectionGradeCell
            key={`projectionGrade-${so5FixtureEvent}-${anyPlayer.slug}`}
            so5FixtureEvent={so5FixtureEvent}
            anyPlayer={anyPlayer}
          />
        )
      )}
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
        mode => (
          <AveragePlayerScoreCell
            key={mode}
            mode={mode}
            anyPlayer={anyPlayer}
            scopedScore={
              scopedScores ? (scopedScores[mode] ?? null) : undefined
            }
          />
        )
      )}
      <SearchPlayerStatsCells
        anyPlayer={anyPlayer}
        scopedBreakdowns={scopedBreakdowns}
        scopedFootballAverageStats={scopedFootballAverageStats}
        scopedFootballTotalStats={scopedFootballTotalStats}
      />
      <CellGrow />
      <StartingPriceCell
        anyPlayer={anyPlayer}
        rarity={rarity}
        onListingsClick={onListingsClick}
      />
    </SearchTableRow>
  );
};

SearchPlayerRow.fragments = {
  anyPlayer: gql`
    fragment SearchPlayerRow_anyPlayer on AnyPlayerInterface {
      slug
      ...ProjectionGradeCell_anyPlayer
      ...SearchPlayerStatsCells_anyPlayer
      ...SearchPlayerCell_anyPlayer
      ...StartingPriceCell_anyPlayer
      ...AnyPlayerUpcomingGames_anyPlayer
      ...AveragePlayerScoreCell_anyPlayer
    }
    ${ProjectionGradeCell.fragments.anyPlayer}
    ${AnyPlayerUpcomingGames.fragments.anyPlayer}
    ${SearchPlayerCell.fragments.anyPlayer}
    ${SearchPlayerStatsCells.fragments.anyPlayer}
    ${StartingPriceCell.fragments.anyPlayer}
    ${AveragePlayerScoreCell.fragments.anyPlayer}
  ` as TypedDocumentNode<SearchPlayerRow_anyPlayer>,
};
